import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Users, Loader, CheckCircle, Rocket, Bell, Target } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPORT_LABELS = { tennis: "Tennis", pickleball: "Pickleball", golf: "Golf", padel: "Padel", squash: "Squash", badminton: "Badminton" };

export default function MarketDetailPage() {
  const { marketId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [market, setMarket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/markets/${marketId}`);
      if (!r.ok) { setError("Market not found."); setLoading(false); return; }
      const d = await r.json();
      setMarket(d);
      if (user && (d.waitlist_users || []).includes(user.id)) setJoined(true);
    } catch { setError("Failed to load market."); }
    finally { setLoading(false); }
  }, [marketId, user]);

  useEffect(() => { load(); }, [load]);

  const joinWaitlist = async () => {
    if (!user) { navigate(`/login?returnTo=${encodeURIComponent(`/markets/${marketId}`)}`); return; }
    setJoining(true); setError("");
    try {
      const r = await fetch(`${API}/markets/${marketId}/waitlist`, { method: "POST", credentials: "include" });
      if (r.ok) { setJoined(true); await load(); }
      else setError("Could not join. Please try again.");
    } catch { setError("Could not join. Please try again."); }
    finally { setJoining(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section"><Loader size={28} className="text-brand-green animate-spin" /></div>;
  if (error && !market) return (
    <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section">
      <div className="text-center"><p className="text-gray-300 mb-4">{error}</p><Link to="/markets" className="text-brand-green hover:underline text-sm">Back to markets</Link></div>
    </div>
  );

  const m = market;
  const threshold = m.launch_threshold?.users || 100;
  const pct = Math.min(100, Math.round((m.waitlist_count / threshold) * 100));
  const isLive = ["active", "healthy", "private_beta"].includes(m.status);

  return (
    <div className="min-h-screen bg-[#0F1629] pt-20 pb-16 dark-section" data-testid="market-detail-page">
      <div className="max-w-3xl mx-auto px-4">
        <Link to="/markets" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4" data-testid="back-to-markets-btn">
          <ArrowLeft size={15} /> All markets
        </Link>

        <div className="bg-gradient-to-br from-[#161B28] to-[#1A2035] border border-white/8 rounded-3xl p-8 mb-6 dark-section">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-3"><MapPin size={15} className="text-brand-green" /> {m.city}</div>
          <h1 className="text-4xl sm:text-5xl font-heading font-black text-white mb-3">{SPORT_LABELS[m.sport] || m.sport}</h1>
          <span className="inline-block text-xs px-3 py-1 rounded-full bg-brand-green/15 text-brand-green border border-brand-green/40">{m.status_label}</span>

          {/* Threshold progress */}
          <div className="mt-8">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-300 flex items-center gap-1.5"><Users size={14} /> {m.waitlist_count} players waiting</span>
              <span className="text-gray-500 flex items-center gap-1.5"><Target size={14} /> {threshold} to launch</span>
            </div>
            <div className="h-3 rounded-full bg-white/8 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Launch progress">
              <div className="h-full bg-gradient-to-r from-brand-green to-emerald-400 rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="market-progress-bar" />
            </div>
            <p className="text-xs text-gray-500 mt-2">{pct}% toward launch</p>
          </div>

          {/* CTA */}
          <div className="mt-8">
            {isLive ? (
              <Link to="/find-partners" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors" data-testid="market-live-cta">
                <Rocket size={16} /> This market is live — find partners
              </Link>
            ) : joined ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green/10 border border-brand-green/30 text-brand-green font-bold rounded-xl" data-testid="market-joined-indicator">
                <CheckCircle size={16} /> You're on the waitlist — we'll email you at launch
              </div>
            ) : (
              <button onClick={joinWaitlist} disabled={joining}
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors disabled:opacity-50"
                data-testid="join-waitlist-btn">
                {joining ? <Loader size={16} className="animate-spin" /> : <Bell size={16} />} Join the waitlist
              </button>
            )}
            {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
          </div>
        </div>

        {/* What launch means */}
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { icon: Users, t: "Matched partners", d: "Players near you, matched by skill and schedule." },
            { icon: MapPin, t: "Local venues", d: "Partner courts and facilities in your city." },
            { icon: Rocket, t: "Group sessions", d: "Open play, mixers and doubles, no partner needed." },
          ].map((f) => (
            <div key={f.t} className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
              <f.icon size={20} className="text-brand-green mb-2" />
              <p className="text-white font-bold text-sm mb-1">{f.t}</p>
              <p className="text-gray-500 text-xs">{f.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
